import { Injectable } from './service.base';
import { action, makeObservable, observable, IObservableArray } from 'mobx';
import { plainToInstance } from 'class-transformer';
import { AxiosResponse } from 'axios';
import moment from 'moment';
import ReactGA from 'react-ga4';
import { api } from '~/tools/api/api';
import { EApiUrl } from '~/tools/api/enums';
import { ModelCreditCard } from '~/models/model.credit.card';
import { ModelUser } from '~/models/model.user';
import { IPaymentResult, ICompleteOrder } from '~/views/types';
import { ModelWdollarPackage } from '~/models/model.wdollar.package';
import { ModelSate } from '~/models/model.state';
import jsonStates from '~/data/states.json';
import { OfferItem, AddressItem } from '~/views/types';
import { GaCategories, GaEvents } from '~/views/enums';
import { extractServerError } from '~/tools/error';

@Injectable()
export class PaysafeFormService {
    @observable public cardNumberValid = false;
    @observable public cardNumberError = false;
    @observable public cardNumberErrorText = '';
    @observable public expiryDateValid = false;
    @observable public expiryDateError = false;
    @observable public expiryDateErrorText = '';
    @observable public cvcValid = false;
    @observable public cvcError = false;
    @observable public cvcErrorText = '';
    @observable public selectedCvcError = false;
    @observable public selectedCvcErrorText = '';
    @observable public isCardHolderInvalid = false;
    @observable public cardHolderErrorText = '';
    @observable public isSelectedCardHolderInvalid = false;
    @observable public selectedCardHolderErrorText = '';

    @observable public btnDisabled = true;
    @observable public cardSaveOptionChecked = true;
    @observable public addNewShowLoading = false;
    @observable public addSelectedCardShowLoading = false;
    @observable public confirmAndPayDisabled = true;
    @observable public creditCardBrand = '';
    @observable public paysafeFieldsError = false;
    @observable public zipError = false;
    @observable public stateError = false;
    @observable public isDateOfBirthInvalid = false;

    @observable public editCardMode = false;
    @observable public addCardMode = false;
    @observable public formTitle = 'Confirm and pay';

    @observable public selectedState = '0';
    @observable public testAmount: any = null;
    @observable public selectedCreditCard: ModelCreditCard;
    @observable public creditCardsExists = false;
    @observable public isFullNameMandatory = false;
    @observable public lastError: { description: string; code?: string } = { description: '', code: undefined };

    public cardHolder = '';
    public selectedCardHolder = '';
    public zip = '';
    public dateBirth: string | null = '';
    public creditCards: IObservableArray<ModelCreditCard> = observable.array([]);

    private paysafejsApiKey: string = process.env.PAYSAFEJS_API_KEY ? process.env.PAYSAFEJS_API_KEY : '';
    public paysafeEnv: string = process.env.PAYSAFEJS_ENV ? process.env.PAYSAFEJS_ENV : 'TEST';
    private paysafeDefaultAccount: number = process.env.PAYSAFEJS_ACCOUNT
        ? (process.env.PAYSAFEJS_ACCOUNT as unknown as number)
        : 0;
    private paysafeInstance: any = null;
    private selectedCardPaysafeInstance: any = null;
    private paysafeFieldsErrorCode = '9003';
    private unsupportedCardErrorCode = '9125';
    public states: IObservableArray<ModelSate> = observable.array([]);
    private supportedCardBrands = ['Visa', 'MasterCard', 'Maestro', 'American Express', 'Discover'];

    constructor() {
        makeObservable(this);
        this.states.replace(plainToInstance(ModelSate, jsonStates));
    }

    @action
    handleCardSaveOptionChange = () => {
        this.cardSaveOptionChecked = !this.cardSaveOptionChecked;
    };

    @action
    payByNewCreditCard = async (
        wdollarPackage: ModelWdollarPackage,
        authService: any,
    ): Promise<IPaymentResult | null> => {
        let paymentResult: IPaymentResult | null = null;
        if (!this.btnDisabled) {
            if (this.addNewShowLoading) {
                return null;
            }

            this.setAddNewShowLoading(true);
            let merchantRefNum = '';
            const dateArr: Array<string> = this.dateBirth ? this.dateBirth.split('/') : [];
            const month: number = dateArr[0] as unknown as number;
            const day: number = dateArr[1] as unknown as number;
            const year: number = dateArr[2] as unknown as number;

            try {
                const stateCode = this.selectedState.split('-').at(1);

                const data = {
                    offerId: +wdollarPackage.id,
                    ticket: wdollarPackage.ticket,
                    countryCode: 'US',
                    areaCode: stateCode,
                    postalCode: this.zip,
                    amount: this.testAmount ? this.testAmount : wdollarPackage.price,
                    dateOfBirth: `${year}-${month}-${day}`,
                };
                const response: AxiosResponse = await api.post(EApiUrl.createPaysafeOrder, data, {
                    headers: {
                        Authorization: authService.currentUser?.token,
                    },
                });
                if (response.status === 200 && response.data?.merchantRefNum) {
                    merchantRefNum = response.data?.merchantRefNum;
                    const tokenizationOptions = {
                        amount: +response.data?.amount,
                        merchantRefNum: merchantRefNum,
                        transactionType: 'PAYMENT',
                        paymentType: 'CARD',
                        customerDetails: {
                            billingDetails: {
                                country: 'US',
                                zip: this.zip,
                                state: stateCode,
                                cardHolderName: this.cardHolder,
                            },
                            profile: {
                                dateOfBirth: {
                                    day: +day,
                                    year: +year,
                                    month: +month,
                                },
                            },
                        },
                    };

                    paymentResult = await this.paysafeInstance.tokenize(tokenizationOptions);
                }
            } catch (error: any) {
                this.setAddNewShowLoading(false);
                if (error?.code === this.paysafeFieldsErrorCode) {
                    this.setPaysafeFieldsError(true);
                    this.handlePaysafeFieldsError(error);
                } else if (error.response && error.response.data && error.response.data.error) {
                    this.setPaysafeFieldsError(true);
                    this.handlePaysafeFieldsError(error.response.data.error);
                } else if (error.response && error.response.status && error.response.status === 403) {
                    authService.setLastError(extractServerError(error?.response?.data ?? error));
                    authService.setShowForbiddenPopup(true);
                } else {
                    paymentResult = { transactionDeclined: true };
                    if (error.code && error.code === this.unsupportedCardErrorCode) {
                        paymentResult.alertHeaderText = 'Transaction Declined';
                        paymentResult.alertMainText =
                            'This card type is not supported. Please use a different card and try again.';
                    }
                }
            }

            try {
                if (paymentResult && !paymentResult?.transactionDeclined) {
                    paymentResult.cardHolder = this.cardHolder;
                    const completeOrderData: ICompleteOrder = {
                        merchantRefNum: merchantRefNum,
                        paymentHandleToken: paymentResult.token,
                        saveCard: this.cardSaveOptionChecked,
                        fullName: this.cardHolder,
                        dateOfBirth: {
                            day: +day,
                            year: +year,
                            month: +month,
                        },
                    };

                    const completePaysafeOrderResponse: AxiosResponse = await api.post(
                        EApiUrl.completePaysafeOrder,
                        completeOrderData,
                        {
                            headers: {
                                Authorization: authService.currentUser?.token,
                            },
                        },
                    );
                    if (completePaysafeOrderResponse.status === 200) {
                        paymentResult.id = completePaysafeOrderResponse.data?.purchaseNumber;
                        paymentResult.paymentMethod = completePaysafeOrderResponse.data?.paymentMethod;
                        paymentResult.total = completePaysafeOrderResponse.data?.total;

                        const completeOrderItems = completePaysafeOrderResponse.data?.items;
                        const wdollar = completeOrderItems.find((record: OfferItem) => record.typ === 'wdollar');
                        paymentResult.packagePrice = wdollar ? wdollar.amount : 0;

                        const freePlay = completeOrderItems.find((record: OfferItem) => record.typ === 'free_play');
                        paymentResult.packageFreePlay = freePlay ? freePlay.amount : 0;

                        this.setCardNumberValid(false);
                        this.btnDisabled = true;
                    }
                }
            } catch (error: any) {
                if (error.response.data.error) {
                    const errorCode = error.response.data.error.primary.code;
                    const serverError = extractServerError(error?.response?.data ?? error);
                    if (errorCode === 'payment_request_declined') {
                        paymentResult = {
                            transactionDeclined: true,
                            alertHeaderText: 'Oops!',
                            alertMainText: serverError.description,
                        };
                    } else if (errorCode === 'payment_error') {
                        paymentResult = {
                            completePurchaseError: true,
                            alertHeaderText: 'Something went wrong',
                            alertMainText: serverError.description,
                        };
                    } else {
                        this.setPaysafeFieldsError(true);
                        this.handlePaysafeFieldsError(error.response.data.error);
                        paymentResult = null;
                    }
                } else {
                    paymentResult = { completePurchaseError: true };
                }

                this.setAddNewShowLoading(false);
                this.btnDisabled = true;
            }
        }

        if (paymentResult && !paymentResult.transactionDeclined && !paymentResult.completePurchaseError) {
            this.deleteCardAfterEdit(authService.currentUser);
        }

        return paymentResult;
    };

    initPaysafe = async () => {
        const paysafeOptions = {
            currencyCode: 'USD',
            accounts: {
                default: +this.paysafeDefaultAccount,
            },
            environment: this.paysafeEnv,
            fields: {
                cardNumber: {
                    selector: '#cardNumber',
                    separator: ' ',
                },
                cvv: {
                    selector: '#cardCVC',
                    mask: false,
                    optional: false,
                },
                expiryDate: {
                    selector: '#cardExpiry',
                    placeholder: 'MM / YY',
                },
            },
            style: {
                input: {
                    color: '#777E90',
                    'font-size': '14px',
                    'line-height': '24px',
                    'letter-spacing': '1px',
                    'text-align': 'left',
                },
                ':focus': {
                    'font-weight': '600',
                    color: '#777E90',
                },
            },
        };

        try {
            // @ts-ignore
            this.paysafeInstance = await paysafe.fields.setup(this.paysafejsApiKey, paysafeOptions);
            const paymentMethods = await this.paysafeInstance.show();
            this.addPaysafeEventListeners();
        } catch (error: any) {
            console.log(error);
        }
    };

    initSelectedCardPaysafe = async () => {
        const paysafeOptions = {
            currencyCode: 'USD',
            accounts: {
                default: +this.paysafeDefaultAccount,
            },
            environment: this.paysafeEnv,
            fields: {
                cardNumber: {
                    selector: '#selectedCardNumber',
                    optional: true,
                },
                cvv: {
                    selector: '#selectedCardCvc',
                },
                expiryDate: {
                    selector: '#selectedCardExpiry',
                    optional: true,
                },
            },
            style: {
                input: {
                    color: '#777E90',
                    'font-size': '14px',
                    'line-height': '24px',
                    'letter-spacing': '1px',
                    'text-align': 'left',
                },
                ':focus': {
                    'font-weight': '600',
                    color: '#777E90',
                },
            },
        };

        try {
            // @ts-ignore
            this.selectedCardPaysafeInstance = await paysafe.fields.setup(this.paysafejsApiKey, paysafeOptions);
            this.selectedCardPaysafeInstance.resetCardDetails(this.selectedCreditCard.cardTypeShorten);
            this.addSelectedPaysafeEventListeners();
        } catch (error: any) {
            console.log(error);
        }
    };

    @action
    setBtnDisabled = (value: boolean) => {
        this.btnDisabled = value;
    };

    @action
    setCardNumberValid = (value: boolean) => {
        this.cardNumberValid = value;
    };

    @action
    setCardNumberError = (value: boolean) => {
        this.cardNumberError = value;
    };

    @action
    setExpiryDateError = (value: boolean) => {
        this.expiryDateError = value;
    };

    @action
    setCvcError = (value: boolean) => {
        this.cvcError = value;
    };

    @action
    addPaysafeEventListeners = () => {
        this.paysafeInstance.fields('cvv cardNumber expiryDate').valid((eventInstance: any, event: any) => {
            this.setPaysafeFieldsError(false);

            switch (event.target.fieldName) {
                case 'CardNumber':
                    if (eventInstance.getCardBrand()) {
                        this.setCardNumberError(false);
                        this.setCardNumberValid(true);
                        this.setCreditCardBrand(eventInstance.getCardBrand());
                        this.setCardNumberErrorText('');
                    } else {
                        this.setCardNumberValid(false);
                        this.setCardNumberError(true);
                        this.setCardNumberErrorText('Invalid Card number');
                        this.setCreditCardBrand('');
                    }
                    break;
                case 'ExpiryDate':
                    this.setExpiryDateError(false);
                    this.setExpiryDateErrorText('');
                    this.setExpiryDateValid(true);
                    break;
                case 'Cvv':
                    this.setCvcError(false);
                    this.setCvcErrorText('');
                    this.setCvcValid(true);
                    break;
                default:
                    break;
            }
            this.validateAllFields();
        });

        this.paysafeInstance.fields('cvv cardNumber expiryDate').invalid((eventInstance: any, event: any) => {
            this.setBtnDisabled(true);

            switch (event.target.fieldName) {
                case 'CardNumber':
                    this.setCardNumberValid(false);
                    this.setCardNumberError(true);
                    this.setCardNumberErrorText('Invalid Card number');
                    this.setCreditCardBrand('');
                    break;
                case 'ExpiryDate':
                    this.setExpiryDateError(true);
                    this.setExpiryDateValid(false);
                    break;
                case 'Cvv':
                    this.setCvcError(true);
                    this.setCvcValid(false);
                    break;
                default:
                    break;
            }
        });

        this.paysafeInstance.fields('cvv cardNumber expiryDate').fieldValueChange((eventInstance: any, event: any) => {
            if (this.cardNumberError && eventInstance.fields.cardNumber.isEmpty()) {
                this.setCardNumberError(false);
                this.setCardNumberErrorText('');
            } else if (eventInstance.fields.cardNumber.isEmpty()) {
                this.setCreditCardBrand('');
            }

            if (this.cvcError && eventInstance.fields.cvv.isEmpty()) {
                this.setCvcError(false);
                this.setCvcErrorText('');
            }

            if (this.expiryDateError && eventInstance.fields.expiryDate.isEmpty()) {
                this.setExpiryDateError(false);
                this.setExpiryDateErrorText('');
            }

            if (event.target.fieldName === 'CardNumber' && eventInstance.getCardBrand()) {
                this.setCreditCardBrand(eventInstance.getCardBrand());
            }
        });

        this.paysafeInstance.fields('cardNumber').blur((eventInstance: any, event: any) => {
            if (
                !eventInstance.fields.cardNumber.isEmpty() &&
                (!eventInstance.fields.cardNumber.isValid() || !eventInstance.getCardBrand())
            ) {
                this.setCardNumberValid(false);
                this.setCardNumberError(true);
                this.setCardNumberErrorText('Invalid Card number');
                this.setCreditCardBrand('');
            }
        });

        this.paysafeInstance.fields('cardNumber').focus((eventInstance: any, event: any) => {
            this.setCardNumberError(false);
            this.setCardNumberErrorText('');
        });
    };

    addSelectedPaysafeEventListeners = () => {
        this.selectedCardPaysafeInstance.fields('cvv').valid((eventInstance: any, event: any) => {
            this.setSelectedCvcError(false);
            this.setSelectedCvcErrorText('');
            this.setCvcValid(true);
            this.selectedValidateAllFields();

            ReactGA.event({
                category: GaCategories.purchaseFlow,
                action: GaEvents.paymentInfoEntered,
                label: JSON.stringify({
                    card_saved_opt_in: false,
                    card_type: this.creditCardBrand,
                    payment_method: 'saved_card',
                }),
            });
        });

        this.selectedCardPaysafeInstance.fields('cvv').invalid((eventInstance: any, event: any) => {
            this.setCvcValid(false);
            this.selectedValidateAllFields();
        });

        this.selectedCardPaysafeInstance.fields('cvv').fieldValueChange((eventInstance: any, event: any) => {
            if (this.cvcError && eventInstance.fields.cvv.isEmpty()) {
                this.setSelectedCvcError(false);
                this.setSelectedCvcErrorText('');
                this.setCvcValid(true);
                this.selectedValidateAllFields();
            }
        });
    };

    @action
    setConfirmAndPayDisabled = (value: boolean) => {
        this.confirmAndPayDisabled = value;
    };

    @action
    getUserCreditCards = async (wdollarPackage: ModelWdollarPackage, authService: any) => {
        this.clearAllErrors();
        this.editCardMode = false;
        this.addCardMode = false;
        if (authService && authService.currentUser && !this.creditCards.length) {
            try {
                const response: AxiosResponse = await api.get(EApiUrl.creditCard, {
                    headers: {
                        Authorization: authService.currentUser?.token,
                    },
                });
                if (response.status === 200 && response.data.paymentHandles) {
                    const creditCards = [];

                    for (const item of response.data.paymentHandles) {
                        const creditCard: ModelCreditCard = {
                            id: item.id,
                            cardNumber: `**** **** **** ${item.card.lastDigits}`,
                            cardExpiration: `${item.card.cardExpiry.month}/${item.card.cardExpiry.year}`,
                            cardType: item.card.cardType === 'VI' ? 'Visa' : item.card.cardType,
                            cardTypeShorten: item.card.cardType,
                            paymentTokenFrom: item.paymentHandleToken,
                        };

                        const dateOfBirth = response.data.dateOfBirth;
                        if (dateOfBirth && dateOfBirth.month) {
                            let monthStr = dateOfBirth.month.toString();
                            if (monthStr.length === 1) {
                                monthStr = `0${monthStr}`;
                            }

                            let dayStr = dateOfBirth.day.toString();
                            if (dayStr.length === 1) {
                                dayStr = `0${dayStr}`;
                            }

                            creditCard.dateOfBirth = `${monthStr}/${dayStr}/${dateOfBirth.year}`;
                        }

                        const addresses = response.data.addresses;
                        if (addresses && addresses.length) {
                            const cardAddress = addresses.find(
                                (addressItem: AddressItem) => addressItem.id === item.billingDetailsId,
                            );

                            if (cardAddress) {
                                creditCard.state = cardAddress.state;
                                creditCard.zip = cardAddress.zip;
                            }
                        }

                        creditCards.push(creditCard);
                    }

                    if (creditCards.length) {
                        this.setCreditCards(creditCards);
                        this.setCreditCardsExists(true);
                        this.setSelectedCreditCard(creditCards[0]);

                        this.isFullNameMandatory = response.data.isFullNameMandatory
                            ? response.data.isFullNameMandatory
                            : false;

                        setTimeout(() => {
                            this.initSelectedCardPaysafe();
                        }, 500);

                        authService.sendGaEvent({
                            category: GaCategories.purchaseFlow,
                            action: GaEvents.checkoutStarted,
                            label: JSON.stringify({
                                saved_card_available: this.creditCardsExists,
                                item_id: wdollarPackage ? wdollarPackage.id : '',
                                price_usd: wdollarPackage ? wdollarPackage.price : '',
                                currency: 'USD',
                            }),
                        });
                    } else {
                        this.setCreditCardsExists(false);
                        setTimeout(() => {
                            this.initPaysafe();
                        }, 500);
                    }
                } else {
                    this.setCreditCardsExists(false);
                    setTimeout(() => {
                        this.initPaysafe();
                    }, 500);
                }
            } catch (error: any) {
                if (error.response && error.response.status && error.response.status === 403) {
                    authService.setLastError(extractServerError(error?.response?.data ?? error));
                    authService.setShowForbiddenPopup(true);
                } else {
                    this.setLastError(extractServerError(error?.response?.data ?? error));
                }
            }
        }
    };

    @action
    setCreditCards = (creditCards: any) => {
        this.creditCards.replace(creditCards);
    };

    payBySelectedCreditCard = async (
        wdollarPackage: ModelWdollarPackage,
        authService: any,
    ): Promise<IPaymentResult | null> => {
        let paymentResult: IPaymentResult | null = null;

        if (!this.confirmAndPayDisabled) {
            if (this.addSelectedCardShowLoading) {
                return null;
            }

            this.setAddSelectedCardShowLoading(true);
            let merchantRefNum = '';
            const dateArr: Array<string> = this.selectedCreditCard.dateOfBirth
                ? this.selectedCreditCard.dateOfBirth.split('/')
                : [];
            const month: number = dateArr[0] as unknown as number;
            const day: number = dateArr[1] as unknown as number;
            const year: number = dateArr[2] as unknown as number;

            try {
                const data = {
                    offerId: +wdollarPackage.id,
                    ticket: wdollarPackage.ticket,
                    countryCode: 'US',
                    areaCode: this.selectedCreditCard.state,
                    postalCode: this.selectedCreditCard.zip,
                    dateOfBirth: `${year}-${month}-${day}`,
                };
                const response: AxiosResponse = await api.post(EApiUrl.createPaysafeOrder, data, {
                    headers: {
                        Authorization: authService.currentUser?.token,
                    },
                });
                if (response.status === 200 && response.data?.merchantRefNum) {
                    merchantRefNum = response.data?.merchantRefNum;

                    const customerTokenResponse: AxiosResponse = await api.post(
                        EApiUrl.createCustomerToken,
                        {
                            paymenthandleID: this.selectedCreditCard.id,
                            merchantRefNum,
                        },
                        {
                            headers: {
                                Authorization: authService.currentUser?.token,
                            },
                        },
                    );

                    if (customerTokenResponse.status === 200 && customerTokenResponse.data?.singleUseCustomerToken) {
                        const tokenizationOptions = {
                            amount: +response.data?.amount,
                            merchantRefNum: merchantRefNum,
                            transactionType: 'PAYMENT',
                            paymentType: 'CARD',
                            singleUseCustomerToken: customerTokenResponse.data?.singleUseCustomerToken,
                            paymentTokenFrom: customerTokenResponse.data?.paymentHandleToken,
                        };

                        paymentResult = await this.selectedCardPaysafeInstance.tokenize(tokenizationOptions);
                    }
                }
            } catch (error: any) {
                if (error.response && error.response.status && error.response.status === 403) {
                    authService.setLastError(extractServerError(error?.response?.data ?? error));
                    authService.setShowForbiddenPopup(true);
                } else {
                    paymentResult = { transactionDeclined: true };
                }
                this.setAddSelectedCardShowLoading(false);
            }

            try {
                if (paymentResult && !paymentResult?.transactionDeclined) {
                    const completeOrderData: ICompleteOrder = {
                        merchantRefNum,
                        paymentHandleToken: paymentResult.token,
                        dateOfBirth: {
                            day: +day,
                            year: +year,
                            month: +month,
                        },
                    };

                    if (this.isFullNameMandatory) {
                        completeOrderData.fullName = this.selectedCardHolder;
                    }

                    const completePaysafeOrderResponse: AxiosResponse = await api.post(
                        EApiUrl.completePaysafeOrder,
                        completeOrderData,
                        {
                            headers: {
                                Authorization: authService.currentUser?.token,
                            },
                        },
                    );
                    if (completePaysafeOrderResponse.status === 200) {
                        paymentResult.id = completePaysafeOrderResponse.data?.purchaseNumber;
                        paymentResult.paymentMethod = completePaysafeOrderResponse.data?.paymentMethod;
                        paymentResult.total = completePaysafeOrderResponse.data?.total;

                        const completeOrderItems = completePaysafeOrderResponse.data?.items;
                        const wdollar = completeOrderItems.find((record: OfferItem) => record.typ === 'wdollar');
                        paymentResult.packagePrice = wdollar ? wdollar.amount : 0;

                        const freePlay = completeOrderItems.find((record: OfferItem) => record.typ === 'free_play');
                        paymentResult.packageFreePlay = freePlay ? freePlay.amount : 0;

                        this.confirmAndPayDisabled = true;
                    }
                }
            } catch (error: any) {
                if (error.response.data.error) {
                    const errorCode = error.response.data.error.primary.code;
                    const serverError = extractServerError(error?.response?.data ?? error);
                    if (errorCode === 'payment_request_declined') {
                        paymentResult = {
                            transactionDeclined: true,
                            alertHeaderText: 'Oops!',
                            alertMainText: serverError.description,
                        };
                    } else if (errorCode === 'payment_error') {
                        paymentResult = {
                            completePurchaseError: true,
                            alertHeaderText: 'Something went wrong',
                            alertMainText: serverError.description,
                        };
                    } else {
                        this.setPaysafeFieldsError(true);
                        this.handlePaysafeFieldsError(error.response.data.error);
                        paymentResult = null;
                    }
                } else {
                    paymentResult = { completePurchaseError: true };
                }

                this.confirmAndPayDisabled = true;
            }
        }

        this.setAddSelectedCardShowLoading(false);

        return paymentResult;
    };

    @action
    setAddSelectedCardShowLoading = (value: boolean) => {
        this.addSelectedCardShowLoading = value;
    };

    @action
    setAddNewShowLoading = (value: boolean) => {
        this.addNewShowLoading = value;
    };

    @action
    handleCardHolderChange = (event: { target: { value: string } }) => {
        this.isCardHolderInvalid = false;
        this.cardHolderErrorText = '';

        if (event.target.value && !/^[a-zA-Z\s]{2,}$/i.test(event.target.value)) {
            this.isCardHolderInvalid = true;
            this.cardHolderErrorText = event.target.value.length === 1 ? 'Minimum 2 letters' : 'Invalid format';
            this.cardHolder = '';
        } else {
            this.isCardHolderInvalid = false;
            this.cardHolder = event.target.value;
            this.cardHolderErrorText = '';
        }

        this.validateAllFields();
    };

    @action
    handleSelectedCardHolderChange = (event: { target: { value: string } }) => {
        this.isSelectedCardHolderInvalid = false;
        this.selectedCardHolderErrorText = '';

        if (event.target.value && !/^[a-zA-Z\s]{2,}$/i.test(event.target.value)) {
            this.isSelectedCardHolderInvalid = true;
            this.selectedCardHolderErrorText = event.target.value.length === 1 ? 'Minimum 2 letters' : 'Invalid format';
            this.selectedCardHolder = '';
        } else {
            this.isSelectedCardHolderInvalid = false;
            this.selectedCardHolder = event.target.value;
            this.selectedCardHolderErrorText = '';
        }

        this.selectedValidateAllFields();
    };

    @action
    setCreditCardBrand = (value: string) => {
        if (value && this.supportedCardBrands.includes(value)) {
            this.creditCardBrand = value;
        } else {
            this.creditCardBrand = '';
        }
    };

    @action
    clearAllErrors = () => {
        this.cardNumberError = false;
        this.setCardNumberErrorText('');

        this.isCardHolderInvalid = false;
        this.cardHolderErrorText = '';
        this.cardHolder = '';
        this.isSelectedCardHolderInvalid = false;
        this.selectedCardHolderErrorText = '';
        this.selectedCardHolder = '';
        this.expiryDateError = false;
        this.setExpiryDateErrorText('');

        this.cvcError = false;
        this.setCvcErrorText('');
        this.setSelectedCvcError(false);
        this.setSelectedCvcErrorText('');
        this.setCardNumberValid(false);
        this.creditCardBrand = '';
        this.setPaysafeFieldsError(false);

        this.selectedState = '0';
        this.zipError = false;
        this.zip = '';

        this.setExpiryDateValid(false);
        this.setCvcValid(false);
        this.stateError = false;
        this.dateBirth = '';
        this.isDateOfBirthInvalid = false;
        this.creditCards = observable.array([]);
    };

    @action
    setPaysafeFieldsError = (value: boolean) => {
        this.paysafeFieldsError = value;
    };

    @action
    setExpiryDateErrorText = (value: string) => {
        this.expiryDateErrorText = value;
    };

    @action
    setCardNumberErrorText = (value: string) => {
        this.cardNumberErrorText = value;
    };

    @action
    setCvcErrorText = (value: string) => {
        this.cvcErrorText = value;
    };

    @action
    handlePaysafeFieldsError = (error: any) => {
        if (error?.fieldErrors && error?.fieldErrors.length) {
            for (const item of error?.fieldErrors) {
                switch (item.field) {
                    case 'expiry date':
                    case 'expiry year':
                    case 'expiry month':
                        this.setExpiryDateErrorText('Enter a valid expiration date');
                        this.setExpiryDateError(true);
                        break;
                    case 'card number':
                        this.setCardNumberErrorText('Enter a valid card number');
                        this.setCardNumberError(true);
                        break;
                    case 'cvv':
                        this.setCvcErrorText('Enter the CVV on your card');
                        this.setCvcError(true);
                        this.setSelectedCvcErrorText('Enter the CVV on your card');
                        this.setSelectedCvcError(true);
                        break;
                }
            }
        } else if (error?.primary?.code) {
            switch (error.primary.code) {
                case 'cvv_check_failed':
                    this.setCvcErrorText('Enter the CVV on your card');
                    this.setCvcError(true);
                    this.setSelectedCvcErrorText('Enter the CVV on your card');
                    this.setSelectedCvcError(true);
                    break;
                case 'expiry_date_invalid':
                    this.setExpiryDateErrorText('Enter a valid expiration date');
                    this.setExpiryDateError(true);
                    break;
                case 'invalid_card_number':
                    this.setCardNumberErrorText('Enter a valid card number');
                    this.setCardNumberError(true);
                    break;
                case 'invalid_postal_code':
                    this.setZipError(true);
                    break;
            }
        }
    };

    @action
    handleStateChange = (event: { target: { value: string } }) => {
        this.stateError = false;
        this.selectedState = event.target.value;

        this.validateAllFields();
    };

    @action
    validateAllFields = () => {
        if (
            !this.isCardHolderInvalid &&
            this.cardHolder &&
            !this.expiryDateError &&
            !this.cvcError &&
            this.selectedState !== '0' &&
            this.zip &&
            this.dateBirth &&
            this.cardNumberValid &&
            this.expiryDateValid &&
            this.cvcValid
        ) {
            this.setBtnDisabled(false);
            ReactGA.event({
                category: GaCategories.purchaseFlow,
                action: GaEvents.paymentInfoEntered,
                label: JSON.stringify({
                    card_saved_opt_in: this.cardSaveOptionChecked,
                    card_type: this.creditCardBrand,
                    payment_method: 'manual_entry',
                }),
            });
        } else {
            this.setBtnDisabled(true);
        }
    };

    @action
    selectedValidateAllFields = () => {
        const cardHolderValid = !this.isFullNameMandatory
            ? true
            : !this.isSelectedCardHolderInvalid && this.selectedCardHolder;
        if (cardHolderValid && !this.selectedCvcError && this.cvcValid) {
            this.setConfirmAndPayDisabled(false);
        } else {
            this.setConfirmAndPayDisabled(true);
        }
    };

    @action
    onZipKeyDown = (e: any) => {
        const numPadNumberKeyCodes = [96, 97, 98, 99, 100, 101, 102, 103, 104, 105, 109];
        const tabBackDel = [8, 9, 46];
        const acv = [65, 67, 86];
        const macProblems = [86, 91, 93];

        if (
            (e.keyCode < 48 || (e.keyCode > 57 && !numPadNumberKeyCodes.includes(e.keyCode))) &&
            !tabBackDel.includes(e.keyCode) &&
            (e.ctrlKey === false || ((e.ctrlKey === true || e.metaKey === true) && !acv.includes(e.keyCode)))
        ) {
            if (macProblems.includes(e.keyCode)) {
                return;
            }

            e.preventDefault();
        }
    };

    @action
    onZipBlur = (value: string) => {
        if (value && /^\d{5}$/i.test(value)) {
            this.zip = value;
            this.zipError = false;
        } else if (value) {
            this.zip = '';
            this.zipError = true;
        }

        this.validateAllFields();
    };

    @action
    onZipChange = (value: string) => {
        if (value && /^\d{5}$/i.test(value)) {
            this.zip = value;
            this.setPaysafeFieldsError(false);
        } else {
            this.zip = '';
        }

        this.validateAllFields();
    };

    @action
    onZipFocus = () => {
        this.zipError = false;
    };

    @action
    handleTestAmountBlur = (event: { target: { value: string } }) => {
        this.testAmount = event.target.value;
    };

    @action
    setZipError = (value: boolean) => {
        this.zipError = value;
    };

    @action
    validateOnDisabledClick = () => {
        if (this.selectedState === '0') {
            this.stateError = true;
        }

        if (!this.cardNumberValid) {
            this.cardNumberError = true;
            this.setCardNumberErrorText('Enter a card number');
        }

        if (!this.expiryDateValid) {
            this.expiryDateError = true;
            this.setExpiryDateErrorText('Enter an expiration date');
        }

        if (!this.cvcValid) {
            this.cvcError = true;
            this.setCvcErrorText('Enter the CVV on your card');
        }

        if (!this.zip) {
            this.zipError = true;
        }

        if (!this.dateBirth) {
            this.isDateOfBirthInvalid = true;
        }

        if (!this.cardHolder) {
            this.isCardHolderInvalid = true;
            this.cardHolderErrorText = 'Enter a card holder';
        }
    };

    @action
    validateOnDisabledClickSelected = () => {
        if (!this.cvcValid) {
            this.setSelectedCvcError(true);
            this.setSelectedCvcErrorText('Enter the CVV on your card');
        }

        if (this.isFullNameMandatory && !this.selectedCardHolder) {
            this.isSelectedCardHolderInvalid = true;
            this.selectedCardHolderErrorText = 'Enter a card holder';
        }
    };

    @action
    setExpiryDateValid = (value: boolean) => {
        this.expiryDateValid = value;
    };

    @action
    setCvcValid = (value: boolean) => {
        this.cvcValid = value;
    };

    @action
    handleDateOfBirthBlur = (event: { target: { value: string } }) => {
        if (event.target.value) {
            const dateArr: Array<string> = event.target.value.split('/');
            if (dateArr.length < 3) {
                this.isDateOfBirthInvalid = true;
            } else {
                const month: number = dateArr[0] as unknown as number;
                const day: number = dateArr[1] as unknown as number;
                const year: number = dateArr[2] as unknown as number;

                const dateFormat = 'YYYY-MM-DD';
                const date = `${year}-${month}-${day}`;
                const toDateFormat = moment(new Date(date)).format(dateFormat);

                if (moment(toDateFormat, dateFormat, true).isValid()) {
                    const currentYear: number = new Date().getFullYear();
                    const allowYear: number = currentYear - 18;
                    const hunderYearAgo: number = currentYear - 100;

                    if (year > allowYear || year < hunderYearAgo) {
                        this.isDateOfBirthInvalid = true;
                    } else {
                        this.dateBirth = event.target.value;
                        this.isDateOfBirthInvalid = false;
                    }
                } else {
                    this.isDateOfBirthInvalid = true;
                }
            }
        } else {
            this.dateBirth = null;
        }
        this.validateAllFields();
    };

    @action
    handleDateOfBirthFocus = () => {
        this.isDateOfBirthInvalid = false;
    };

    @action
    handleDateOfBirthChange = (event: { target: { value: string } }) => {
        const dateOfBirth = event.target.value;
        if (dateOfBirth && dateOfBirth.length === 10) {
            this.handleDateOfBirthBlur(event);
        } else {
            this.dateBirth = null;
            this.validateAllFields();
        }
    };

    @action
    editCreditCard = () => {
        this.editCardMode = true;
        this.formTitle = 'Edit payment method';
        setTimeout(() => {
            this.initPaysafe();
        }, 1000);

        if (this.selectedCreditCard) {
            this.dateBirth = this.selectedCreditCard.dateOfBirth as string;
            setTimeout(() => {
                this.setSelectedState(`US-${this.selectedCreditCard.state}`);
                this.dateBirth = this.selectedCreditCard.dateOfBirth as string;
                this.zip = this.selectedCreditCard.zip as string;
            }, 1000);
        }
    };

    @action
    addCreditCard = (currentUser: ModelUser | null) => {
        this.addCardMode = true;
        setTimeout(() => {
            this.initPaysafe();
        }, 1000);
    };

    @action
    setSelectedState = (value: string) => {
        this.selectedState = value;
    };

    @action
    handleCardChange = (event: { target: { value: string } }) => {
        this.selectedCreditCard = this.creditCards.find(item => item.id === event.target.value) as ModelCreditCard;
        this.selectedCardPaysafeInstance.resetCardDetails(this.selectedCreditCard.cardTypeShorten);
    };

    @action
    deleteCardAfterEdit = (currentUser: ModelUser | null) => {
        if (this.editCardMode && this.selectedCreditCard) {
            try {
                api.post(
                    EApiUrl.creditCardDelete,
                    { paymentHandleId: this.selectedCreditCard.id },
                    {
                        headers: {
                            Authorization: currentUser?.token,
                        },
                    },
                );
            } catch (error: any) {
                this.setLastError(extractServerError(error?.response?.data ?? error));
            }
        }
    };

    @action
    deleteCreditCard = async (wdollarPackage: ModelWdollarPackage, authService: any) => {
        if (authService && authService.currentUser && this.selectedCreditCard) {
            try {
                await api.post(
                    EApiUrl.creditCardDelete,
                    { paymentHandleId: this.selectedCreditCard.id },
                    {
                        headers: {
                            Authorization: authService.currentUser?.token,
                        },
                    },
                );

                this.getUserCreditCards(wdollarPackage, authService);
            } catch (error: any) {
                if (error.response && error.response.status && error.response.status === 403) {
                    authService.setLastError(extractServerError(error?.response?.data ?? error));
                    authService.setShowForbiddenPopup(true);
                } else {
                    this.setLastError(extractServerError(error?.response?.data ?? error));
                }
            }
        }
    };

    @action
    setCreditCardsExists = (value: boolean) => {
        this.creditCardsExists = value;
    };

    @action
    setSelectedCreditCard = (card: ModelCreditCard) => {
        this.selectedCreditCard = card;
    };

    @action
    setSelectedCvcError = (value: boolean) => {
        this.selectedCvcError = value;
    };

    @action
    setSelectedCvcErrorText = (value: string) => {
        this.selectedCvcErrorText = value;
    };

    @action
    setLastError = (err: { description: string; code?: string }) => {
        this.lastError = err;
    };

    @action
    clearLastError = () => {
        this.lastError = { description: '', code: undefined };
    };
}
